import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

export type PeopleSearchField = 'displayName' | 'gmid' | 'email' | 'org';

export interface GraphPerson {
  id?: string;
  gmid?: string;
  onPremisesSamAccountName?: string;
  displayName?: string;
  mail?: string;
  email?: string;
  userPrincipalName?: string;
  department?: string;
  companyName?: string;
  jobTitle?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PeopleService {

  constructor(private _http: HttpClient) { }

  // handling history of submissions
  //  getHistory(): Observable<any[]> {
  //   return this.http.get<any[]>(this.historyUrl);
  // }

  // saveSubmission(entry: any): Observable<any> {
  //   return this.http.post<any>(this.historyUrl, entry);
  // }

  getPersonByGMID(id: any) {
    const rawId = `${id ?? ''}`.trim();
    const escapedId = rawId.replace(/'/g, "''");
    const baseUrl = environment.peopleSync.url.endsWith('/users')
      ? environment.peopleSync.url
      : `${environment.peopleSync.url}/users`;
    const params = new HttpParams()
      .set('$filter', `startswith(onPremisesSamAccountName,'${escapedId}')`)
      .set('$count', 'true');
    const headers = new HttpHeaders({
      'ConsistencyLevel': 'eventual'
    });

    return this._http.get<any>(baseUrl, { params, headers }).pipe(
      map((response: any) => Array.isArray(response?.value) ? response.value : [])
    );
  }

  searchPeople(field: PeopleSearchField, query: string): Observable<GraphPerson[]> {
    const value = `${query ?? ''}`.trim();
    if (!value) {
      return of([]);
    }

    const escapedValue = value.replace(/'/g, "''");
    const filters: Record<PeopleSearchField, string> = {
      displayName: `startswith(displayName,'${escapedValue}')`,
      gmid: `startswith(onPremisesSamAccountName,'${escapedValue}')`,
      email: `startswith(mail,'${escapedValue}') or startswith(userPrincipalName,'${escapedValue}')`,
      org: `startswith(department,'${escapedValue}') or startswith(companyName,'${escapedValue}')`
    };
    const params = new HttpParams()
      .set('$filter', filters[field])
      .set('$select', 'id,displayName,mail,userPrincipalName,onPremisesSamAccountName,department,companyName,jobTitle')
      .set('$top', '25')
      .set('$count', 'true');
    const headers = new HttpHeaders({
      'ConsistencyLevel': 'eventual'
    });

    return this._http.get<{ value?: GraphPerson[] }>(this.peopleSyncUsersUrl(), { params, headers }).pipe(
      map(response => (response.value ?? []).map(person => ({
        ...person,
        gmid: person.gmid || person.onPremisesSamAccountName || '',
        email: person.mail || person.userPrincipalName || ''
      })))
    );
  }

  getPhotoByGmid(gmid: string): Observable<string | null> {
    const normalizedGmid = `${gmid ?? ''}`.trim();
    if (!normalizedGmid) {
      return of(null);
    }

    return this.searchPeople('gmid', normalizedGmid).pipe(
      map(users => users.find(user => user.gmid?.toLowerCase() === normalizedGmid.toLowerCase())?.id ?? null),
      switchMap(userId => userId ? this.getPhotoByUserId(userId) : of(null)),
      catchError(() => of(null))
    );
  }

  getPhotoByUserId(userId: string): Observable<string | null> {
    const id = `${userId ?? ''}`.trim();
    if (!id) {
      return of(null);
    }

    const baseUrl = environment.peopleSync.url.endsWith('/users')
      ? environment.peopleSync.url
      : `${environment.peopleSync.url}/users`;

    const photoUrl = `${baseUrl}/${encodeURIComponent(id)}/photo/$value`;

    return this._http.get(photoUrl, { responseType: 'blob' }).pipe(
      switchMap((blob: Blob) => from(this.blobToDataUrl(blob))),
      catchError(() => of(null))
    );
  }

  private blobToDataUrl(blob: Blob): Promise<string | null> {
    return new Promise((resolve) => {
      if (!blob || blob.size === 0) {
        resolve(null);
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  }
  
  getPersonByEmail(email: any) {
    return this._http.get<any[]>(`${environment.peopleSync.url}${environment.peopleSync.endpoints.email}/${email}`);
  }

  getPersonByGMIN(gmin: any) {
    return this._http.get<any[]>(`${environment.peopleSync.url}${environment.peopleSync.endpoints.gmin}/${gmin}`);
  }

  private peopleSyncUsersUrl(): string {
    return environment.peopleSync.url.endsWith('/users')
      ? environment.peopleSync.url
      : `${environment.peopleSync.url}/users`;
  }
}
